<template>
  <transition
    name="overlay-fade"
    enter-active-class="transition-all duration-400"
    leave-active-class="transition-all duration-400"
    enter-from-class="opacity-0"
    leave-to-class="opacity-0 transition-all duration-0"
  >
    <div
      v-show="isShow"
      class="fixed left-0 top-0 w-full h-screen bg-black bg-opacity-20 z-40 opacity-100 box-border"
      :class="{ 'overflow-hidden w-screen': isShow }"
    >
      <div
        :class="containerClass"
        @mousedown.self="handleBackgroundClick"
        @touchstart.self="handleBackgroundClick"
      >
        <div
          v-hotkey="{ escape: closeModal }"
          :style="{ width, height }"
          :class="[
            minWidthClass,
            maxWidthClass,
            fullScreenClass,
            scrollable ? 'overflow-auto mb-0.5' : 'overflow-hidden',
            {
              'flex justify-center items-center': centered
            },
            noPadding ? 'p-0' : 'md:px-4 px-6 md:py-5 pt-4'
          ]"
          class="rounded-2xl relative max-h-screen bg-white min-h-32 box-border"
        >
          <button
            :class="closeButtonClass"
            @click="handleCloseClick"
          >
            <CloseIcon />
          </button>
          <LoadingIconGrid
            v-if="isLoading"
            class="mx-auto my-20"
          />
          <div
            v-if="loadError && !isLoading"
            class="p-8 text-center text-red-600"
          >
            <p class="text-lg font-medium mb-2">Ошибка загрузки</p>
            <p class="text-sm opacity-75">{{ loadError }}</p>
          </div>
          <component
            :is="asyncComponent"
            v-if="isShow && asyncComponent && !isLoading && !loadError"
            v-bind="modalProps"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, markRaw, ref, watch } from 'vue'
import CloseIcon from '~/assets/svg/close.svg?component'
import LoadingIconGrid from '~/assets/svg/spinners/grid.svg?component'
const { $breakpoints } = useNuxtApp()
const { $modal } = useNuxtApp()

const { isShow, path, modalProps } = useModal()

const isLoading = ref(false)
const asyncComponent = ref<unknown | null>(null)
const loadError = ref<string | null>(null)

const noPadding = computed(() => modalProps.value?.noPadding)
const desktopLikeMobile = computed(() => modalProps.value?.desktopLikeMobile)
const width = computed(() => {
  if (desktopLikeMobile.value) {
    return 'auto'
  }
  return $breakpoints.lMd ? 'auto' : '100vmin'
})
const height = computed(() => {
  if (desktopLikeMobile.value) {
    return 'auto'
  }
  return $breakpoints.lMd ? 'auto' : '100vh'
})
const fullScreenClass = computed(() => {
  if (desktopLikeMobile.value) {
    return ''
  }
  return $breakpoints.lMd ? '' : 'rounded-none m-0 left-0 top-0'
})
const centered = computed(() => modalProps.value?.centered)
const scrollable = computed(() => modalProps.value?.scrollable ?? true)
const minWidthClass = computed(() => modalProps.value?.minWidthClass || 'sm:min-w-120')
const maxWidthClass = computed(() => modalProps.value?.maxWidthClass || '')
const closeButtonClass = computed(() => {
  if (desktopLikeMobile.value) {
    return 'p-1 absolute top-5 right-5 z-50 m-0 cursor-pointer text-gray-800 hover:text-gray-600 active:text-gray-900 hover:scale-110  transition-all duration-300'
  }
  return 'p-1 sm:absolute fixed top-5 right-5 z-50 m-0 cursor-pointer text-gray-800 hover:text-gray-600 active:text-gray-900 hover:scale-110 transition-all duration-300'
})
const containerClass = computed(() => {
  if (desktopLikeMobile.value) {
    return 'w-full min-h-full h-auto flex items-center justify-center px-2 py-7'
  }
  return 'w-full min-h-full h-auto flex items-center justify-center'
})

const handleBackgroundClick = () => {
  closeModal()
}

const handleCloseClick = () => {
  closeModal()
}

const closeModal = () => {
  $modal.hide()
}

const componentMap = {
  'shared/modal/change_deposit': () => import('~/components/shared/modal/change_deposit.vue'),
  'shared/modal/telegram_bot': () => import('~/components/shared/modal/telegram_bot.vue'),
  'shared/modal/confirmation': () => import('~/components/shared/modal/confirmation.vue'),
  'shared/modal/change_value': () => import('~/components/shared/modal/change_value.vue'),
  'shared/modal/change_select': () => import('~/components/shared/modal/change_select.vue'),
  'shared/modal/enter_name': () => import('~/components/shared/modal/enter_name.vue'),
  'shared/modal/map': () => import('~/components/shared/modal/map.vue'),
  'shared/modal/elka': () => import('~/components/shared/modal/elka.vue'),
  'shared/modal/promo_code': () => import('~/components/shared/modal/promo_code.vue'),
  'shared/modal/fast_order': () => import('~/components/shared/modal/fast_order.vue'),
  'shared/modal/adult_confirmation': () =>
    import('~/components/shared/modal/adult_confirmation.vue'),
  'shared/modal/change_delivery': () => import('~/components/shared/modal/change_delivery.vue'),
  'shared/modal/change_return_delivery': () =>
    import('~/components/shared/modal/change_return_delivery.vue'),
  'shared/modal/order_rejection': () => import('~/components/shared/modal/order_rejection.vue'),
  'shared/modal/new_payment': () => import('~/components/shared/modal/new_payment.vue'),
  'shared/modal/change_rouge': () => import('~/components/shared/modal/change_rouge.vue'),
  'shared/modal/change_owner_filter': () =>
    import('~/components/shared/modal/change_owner_filter.vue'),
  'shared/rm_confirm_phone': () => import('~/components/shared/rm_confirm_phone.vue'),
  'shared/rm_restore_sucess': () => import('~/components/shared/rm_restore_sucess.vue'),
  'shared/rm_confirm_email': () => import('~/components/shared/rm_confirm_email.vue'),
  'flow/auth': () => import('~/components/flow/auth.vue'),
  'flow/cart_to_checkout': () => import('~/components/flow/cart_to_checkout.vue'),
  'flow/publish': () => import('~/components/flow/publish.vue'),
  'flow/new_payout': () => import('~/components/flow/new_payout.vue'),
  'flow/get_order_single_lot': () => import('~/components/flow/get_order_single_lot.vue'),
  'flow/go_chat_from_lot': () => import('~/components/flow/go_chat_from_lot.vue'),
  'lots/show/date_select': () => import('~/components/lots/show/date_select.vue'),
  'catalog/filter_widget/modal': () => import('~/components/catalog/filter_widget/modal.vue'),
  'orders/review': () => import('~/components/orders/review.vue'),
  'admin/characteristic/modal/item_edit': () =>
    import('~/components/admin/characteristic/modal/item_edit.vue'),
  'admin/characteristic/modal/item_create': () =>
    import('~/components/admin/characteristic/modal/item_create.vue'),
  'admin/characteristic/modal/characteristic_create': () =>
    import('~/components/admin/characteristic/modal/characteristic_create.vue'),
  'admin/characteristic/modal/characteristic_bind': () =>
    import('~/components/admin/characteristic/modal/characteristic_bind.vue'),
  'admin/characteristic/modal/characteristic_items_bind': () =>
    import('~/components/admin/characteristic/modal/characteristic_items_bind.vue'),
  'admin/users/user_type': () => import('~/components/admin/users/user_type.vue'),
  'header/city_pick/city_menu': () => import('~/components/header/city_pick/city_menu.vue'),
  'lots/edit/hourly_prices': () => import('~/components/lots/edit/hourly_prices.vue'),
  'shared/modal/add_tag': () => import('~/components/shared/modal/add_tag.vue'),
  'shared/rm_user_phone': () => import('~/components/shared/rm_user_phone.vue'),
  'shared/modal/simple_text': () => import('~/components/shared/modal/simple_text.vue'),
  'promo/elka/location': () => import('~/components/promo/elka/location/index.vue'),
  'shared/modal/admin_select_payment_method': () => import('~/components/shared/modal/admin_select_payment_method.vue'),
  'shared/modal/admin_select_payout_method': () => import('~/components/shared/modal/admin_select_payout_method.vue'),
  'lots/show/insurance_details_modal': () => import('~/components/lots/show/insurance_details_modal.vue'),
  'admin/insurances/modal/create_batch': () =>
    import('~/components/admin/insurances/modal/create_batch.vue'),
  'admin/insurances/modal/download_registry': () =>
    import('~/components/admin/insurances/modal/download_registry.vue'),
}

const createAsyncComponent = async (componentPath: string) => {
  isLoading.value = true
  asyncComponent.value = null
  loadError.value = null

  try {
    if (componentMap[componentPath as keyof typeof componentMap]) {
      const component = await componentMap[componentPath as keyof typeof componentMap]()

      const resolvedComponent = component.default || component

      asyncComponent.value = markRaw(resolvedComponent)
      isLoading.value = false
    } else {
      loadError.value = `Компонент не найден: ${componentPath}`
      isLoading.value = false
    }
  } catch (error) {
    loadError.value = `Не удалось загрузить компонент: ${componentPath} (${(error as Error).message})`
    isLoading.value = false
  }
}

watch(
  path,
  newValue => {
    if (isShow.value && newValue) {
      createAsyncComponent(newValue as unknown as string)
    }
  },
  { immediate: true },
)

watch(
  isShow,
  newValue => {
    if (newValue && path.value) {
      createAsyncComponent(path.value)
    }
  },
  { immediate: true },
)
</script>
